import type { Bundle } from "@medplum/fhirtypes";
import type { PaginationMetadata } from "../types/pagination-metadata";
import {
  fetchSandboxPatientFirstName,
  SANDBOX_PERSONA_NAMES,
  SandboxPersonaName,
} from "./sandbox-patient";

type CareGapsResponse = { metadata: PaginationMetadata; results: Bundle[] };

const MEASURE_BASE = "https://ncqa.org/fhir/Measure";
const MEASURE_GROUP_SYSTEM = "https://ncqa.org/fhir/CodeSystem/measure-group";
const PERIOD_2025 = { start: "2025-01-01", end: "2025-12-31" };

type ParameterEntry =
  | { name: string; valuePeriod: { start: string; end: string } }
  | { name: string; valueBoolean: boolean }
  | { name: string; valueInteger: number }
  | { name: string; valueString: string };

function makeCareGapBundle(patientId: string, measureId: string, params: ParameterEntry[]): Bundle {
  return {
    resourceType: "Bundle",
    type: "collection",
    entry: [
      {
        fullUrl: `Patient/${patientId}`,
        resource: {
          resourceType: "Patient",
          id: patientId,
        },
      },
      {
        fullUrl: `MeasureReport/${measureId}-${patientId}`,
        resource: {
          resourceType: "MeasureReport",
          id: `${measureId}-${patientId}`,
          extension: [{ url: "job-id", valueString: `job-${measureId}-${patientId}` }],
          status: "complete",
          type: "individual",
          measure: `${MEASURE_BASE}/${measureId}`,
          subject: { reference: `Patient/${patientId}` },
          period: { start: PERIOD_2025.start, end: PERIOD_2025.end },
          group: [
            {
              id: "group-1",
              code: { coding: [{ system: MEASURE_GROUP_SYSTEM, code: measureId }] },
              population: [
                {
                  code: { coding: [{ system: MEASURE_GROUP_SYSTEM, code: "initial-population" }] },
                  count: 1,
                },
                {
                  code: { coding: [{ system: MEASURE_GROUP_SYSTEM, code: "denominator" }] },
                  count: 1,
                },
                {
                  code: {
                    coding: [{ system: MEASURE_GROUP_SYSTEM, code: "denominator-exclusion" }],
                  },
                  count: 0,
                },
                {
                  code: { coding: [{ system: MEASURE_GROUP_SYSTEM, code: "numerator" }] },
                  count: 0,
                },
              ],
            },
          ],
        },
      },
      {
        fullUrl: `Parameters/${measureId}-params-${patientId}`,
        resource: {
          resourceType: "Parameters",
          id: `${measureId}-params-${patientId}`,
          parameter: params,
        },
      },
    ],
  };
}

function buildPersonaBundles(patientId: string): Record<string, Bundle[]> {
  return {
    // Jane (age 28): depression + cervical cancer screening gaps
    jane: [
      makeCareGapBundle(patientId, "DepressionScreeningAndFollowUp", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: false },
        { name: "Depression screening performed", valueBoolean: false },
        { name: "Follow-up within 30 days of positive screen", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age Stratification - 12 to 17", valueBoolean: false },
        { name: "Age Stratification - 18+", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Black or African American", valueBoolean: false },
      ]),
      makeCareGapBundle(patientId, "CervicalCancerScreening", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: false },
        { name: "Is female", valueBoolean: true },
        { name: "Has hysterectomy with no residual cervix", valueBoolean: false },
        { name: "Pap smear in last 3 years", valueBoolean: false },
        { name: "HPV test with Pap smear in last 5 years", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age Stratification - 21 to 64", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Hispanic or Latino", valueBoolean: false },
      ]),
    ],
    // Chris (age 29): blood pressure control + depression screening gaps (documented CKD + hypertension)
    chris: [
      makeCareGapBundle(patientId, "ControllingHighBloodPressure", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: false },
        { name: "Has hypertension diagnosis", valueBoolean: true },
        { name: "BP measurement in measurement period", valueBoolean: true },
        { name: "Most recent systolic BP < 140 mmHg", valueBoolean: false },
        { name: "End-stage renal disease exclusion", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age Stratification - 18 to 59", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Black or African American", valueBoolean: true },
      ]),
      makeCareGapBundle(patientId, "DepressionScreeningAndFollowUp", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: false },
        { name: "Depression screening performed", valueBoolean: false },
        { name: "Follow-up within 30 days of positive screen", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age Stratification - 12 to 17", valueBoolean: false },
        { name: "Age Stratification - 18+", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Black or African American", valueBoolean: true },
      ]),
    ],
    // Ollie (age 78): care for older adults + drug/alcohol screening gaps
    ollie: [
      makeCareGapBundle(patientId, "CareForOlderAdults", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: true },
        { name: "Age 66+", valueBoolean: true },
        { name: "Advance care planning performed", valueBoolean: false },
        { name: "Functional status assessment performed", valueBoolean: false },
        { name: "Pain assessment performed", valueBoolean: false },
        { name: "Medication review performed", valueBoolean: true },
        { name: "Fall risk assessment performed", valueBoolean: false },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - White", valueBoolean: true },
      ]),
      makeCareGapBundle(patientId, "DrugAndAlcoholMisuseAnnualEngagement", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: true },
        { name: "Has substance use disorder diagnosis", valueBoolean: false },
        { name: "Alcohol misuse screening performed", valueBoolean: false },
        { name: "Drug misuse screening performed", valueBoolean: false },
        { name: "Brief intervention or counseling performed", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age 18+", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - White", valueBoolean: true },
      ]),
    ],
    // Andreas (age 72): glycemic status + eye exam gaps for diabetes
    andreas: [
      makeCareGapBundle(patientId, "GlycemicStatusAssessmentForPatientsWithDiabetes", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: true },
        { name: "Has diabetes diagnosis", valueBoolean: true },
        { name: "HbA1c test performed", valueBoolean: true },
        { name: "Most recent HbA1c < 8%", valueBoolean: false },
        { name: "Most recent HbA1c >= 9%", valueBoolean: true },
        { name: "HbA1c poor control (>= 9%)", valueBoolean: true },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age Stratification - 18 to 75", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Hispanic or Latino", valueBoolean: true },
      ]),
      makeCareGapBundle(patientId, "EyeExamForPatientsWithDiabetes", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: true },
        { name: "Has diabetes diagnosis", valueBoolean: true },
        { name: "Retinal eye exam in measurement period", valueBoolean: false },
        { name: "Retinal exam in prior year with negative result", valueBoolean: false },
        { name: "Has diabetic retinopathy diagnosis", valueBoolean: false },
        { name: "Teleretinal imaging performed", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age Stratification - 18 to 75", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Hispanic or Latino", valueBoolean: true },
      ]),
    ],
    // Kyla (age 97): blood pressure control + care for older adults gaps (documented hypertension)
    kyla: [
      makeCareGapBundle(patientId, "ControllingHighBloodPressure", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: true },
        { name: "Has hypertension diagnosis", valueBoolean: true },
        { name: "BP measurement in measurement period", valueBoolean: true },
        { name: "Most recent systolic BP < 140 mmHg", valueBoolean: false },
        { name: "End-stage renal disease exclusion", valueBoolean: false },
        { name: "Pregnancy exclusion", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age Stratification - 18 to 59", valueBoolean: false },
        { name: "Age Stratification - 60 to 85", valueBoolean: false },
        { name: "Age 86+", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Black or African American", valueBoolean: false },
      ]),
      makeCareGapBundle(patientId, "CareForOlderAdults", [
        { name: "Measurement Period", valuePeriod: PERIOD_2025 },
        { name: "Care Gap - Standard", valueBoolean: true },
        { name: "Care Gap - Medicare", valueBoolean: true },
        { name: "Age 66+", valueBoolean: true },
        { name: "Advance care planning performed", valueBoolean: false },
        { name: "Functional status assessment performed", valueBoolean: false },
        { name: "Pain assessment performed", valueBoolean: false },
        { name: "Medication review performed", valueBoolean: true },
        { name: "Fall risk assessment performed", valueBoolean: false },
        { name: "Continuous enrollment", valueBoolean: true },
        { name: "Age 86+", valueBoolean: true },
        { name: "SES - Rural", valueBoolean: false },
        { name: "Race - Black or African American", valueBoolean: false },
      ]),
    ],
  };
}

export async function getSandboxCareGaps(patientId: string): Promise<CareGapsResponse> {
  const firstName = await fetchSandboxPatientFirstName(patientId);
  if (firstName && !SANDBOX_PERSONA_NAMES.includes(firstName.toLowerCase() as SandboxPersonaName)) {
    console.warn(
      `[care-gaps-mock] unknown sandbox persona firstName="${firstName}", fallback=Jane`
    );
  }
  const personaBundles = buildPersonaBundles(patientId);
  const personaKey =
    firstName !== null && firstName !== undefined ? firstName.toLowerCase() : undefined;
  const bundles: Bundle[] =
    (personaKey !== null && personaKey !== undefined ? personaBundles[personaKey] : undefined) ??
    [];
  return {
    metadata: { total: bundles.length, limit: 100, offset: 0 },
    results: bundles,
  };
}
